import { capitalize } from 'lodash'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { CostCenterSelector } from 'modules/finance/components/CostCenterSelector'
import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { FilterValue } from 'modules/finance/components/FinancialWorkspace/types/filter'
import { LocationSelector } from 'modules/finance/components/LocationSelector'

import EmployeeSelector from './EmployeeSelector'
import { LegalEntityMultiSelector } from './LegalEntityMultiSelector'

export const useDefaultFilters = (recentlyAddedFilter: string | undefined) => {
  const { costCenters, savedFilters, saveFilters } = useFilterProvider()
  const { dispatch, state } = useFinancialWorkspace()

  return useUnsafeMemo((): Record<string, FilterValue> => {
    return {
      legalEntityFilter: {
        component: (
          <LegalEntityMultiSelector
            selectedLegalEntities={state.selectedLegalEntityIds}
            defaultLegalEntities={savedFilters.selectedLegalEntityIds}
            onSelectLegalEntities={(legalEntityIds) => {
              dispatch({ type: 'SET_LEGAL_ENTITIES', payload: legalEntityIds })
              saveFilters('selectedLegalEntityIds', legalEntityIds)
            }}
          />
        ),
        label: i18n.t('finance.invoices.detail.fields.legal_entity'),
        value: 'legalEntityFilter',
        key: 'selectedLegalEntityIds',
        grouped: false,
        onReset: () => dispatch({ type: 'SET_LEGAL_ENTITIES' }),
      },
      costCenterFilter: {
        component: (
          <CostCenterSelector
            costCenters={costCenters}
            selectedCostCenterIds={state.costCenterIds}
            defaultCostCenterIds={savedFilters.costCenterIds}
            composeActiveLabel={(selected) => `(${selected.length})`}
            onChange={(value) => {
              dispatch({ type: 'SET_COST_CENTERS', payload: value })
              saveFilters('costCenterIds', value)
            }}
            defaultOpen={recentlyAddedFilter === 'costCenterFilter'}
          />
        ),
        label: i18n.t('bookkeepers.incidence.filters.types.cost_center'),
        value: 'costCenterFilter',
        key: 'costCenterIds',
        visible: costCenters.length > 0,
        onReset: () => dispatch({ type: 'SET_COST_CENTERS' }),
      },
      employeeFilter: {
        component: (
          <EmployeeSelector
            defaultEmployees={savedFilters.selectedEmployees}
            defaultOpen={recentlyAddedFilter === 'employeeFilter'}
            onChange={(value) => {
              dispatch({ type: 'SET_EMPLOYEES', payload: value?.map((i) => i.id) })
              saveFilters('selectedEmployees', value)
            }}
          />
        ),
        label: capitalize(i18n.t('common.person.other')),
        value: 'employeeFilter',
        key: 'selectedEmployeeIds',
        onReset: () => dispatch({ type: 'SET_EMPLOYEES' }),
      },
      locationFilter: {
        component: (
          <LocationSelector
            selectedLocationIds={state.locationIds}
            defaultLocationIds={savedFilters.locationIds}
            onChangeLocationIds={(value) => {
              dispatch({ type: 'SET_LOCATIONS', payload: value })
              saveFilters('locationIds', value)
            }}
            defaultOpen={recentlyAddedFilter === 'locationFilter'}
            composeActiveLabel={(selected) => `(${selected.length})`}
            defaultLabel={i18n.t('finance.financial_workspace.filters.workplace')}
          />
        ),
        label: i18n.t('settings.locations.title'),
        value: 'locationFilter',
        key: 'locationIds',
        onReset: () => dispatch({ type: 'SET_LOCATIONS' }),
      },
    }
  }, [state, dispatch, saveFilters, costCenters, savedFilters, recentlyAddedFilter])
}
